import { useEffect } from 'react'
import { X, Mic, Brain, Star, AlertTriangle, MessageCircle } from 'lucide-react'
import type { CallAnalysis } from '../../../types'

interface Props {
  open: boolean
  onClose: () => void
  callDescription: string
  transcript: string | null | undefined
  analysis: CallAnalysis | null | undefined
}

const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'var(--success-text)',
  neutral:  'var(--text-secondary)',
  negative: 'var(--danger-text)',
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(Math.min(100, Math.max(0, value * 10)))
  const color = pct >= 70 ? 'var(--success-text)' : pct >= 40 ? 'var(--warning-text)' : 'var(--danger-text)'
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs">
        <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ color }} className="font-semibold">{value}/10</span>
      </div>
      <div className="rounded-full overflow-hidden" style={{ height: 4, background: 'var(--border-default)' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 9999 }} />
      </div>
    </div>
  )
}

export default function CallTranscriptDrawer({ open, onClose, callDescription, transcript, analysis }: Props) {
  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const hasTranscript = !!transcript
  const hasAnalysis   = !!analysis

  return (
    <>
      {/* Backdrop — solid enough to block background content on mobile */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.55)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 220ms ease',
          WebkitBackfaceVisibility: 'hidden',
        }}
      />

      {/* Drawer — flex column; only the body div scrolls, header stays pinned */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 201,
          width: 'min(480px, 100vw)',
          display: 'flex',
          flexDirection: 'column',
          /* Explicit solid fallback so --surface-default transparency never bleeds */
          backgroundColor: 'var(--surface-card, var(--surface-default, #1a1d24))',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.24)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 280ms cubic-bezier(0.4,0,0.2,1)',
          /* No overflow here — prevents sticky issues and see-through on WebView */
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — flex-shrink:0 keeps it always visible above the scroll body */}
        <div
          className="flex items-start justify-between gap-3 p-4"
          style={{
            flexShrink: 0,
            borderBottom: '1px solid var(--border-default)',
            backgroundColor: 'var(--surface-card, var(--surface-default, #1a1d24))',
          }}
        >
          <div className="min-w-0">
            <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Call Intelligence
            </p>
            <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-tertiary)' }}>
              {callDescription}
            </p>
          </div>
          <button className="btn-icon shrink-0" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body — this is the only part that scrolls */}
        <div
          className="space-y-5"
          style={{
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: '16px 16px env(safe-area-inset-bottom, 24px)',
          }}
        >
          {!hasTranscript && !hasAnalysis && (
            <div className="empty-state" style={{ marginTop: 40 }}>
              <Mic className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
              <p style={{ color: 'var(--text-tertiary)' }}>No transcript or AI analysis available for this call.</p>
            </div>
          )}

          {/* AI Analysis */}
          {hasAnalysis && (
            <div className="space-y-4">
              {/* Overview */}
              <div
                className="rounded-xl p-3 space-y-2"
                style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
              >
                <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
                  <Brain className="h-3.5 w-3.5" /> Overview
                </p>
                {analysis!.language && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Language</span>
                    <span style={{ color: 'var(--text-primary)' }} className="font-medium">{analysis!.language}</span>
                  </div>
                )}
                {analysis!.customer_sentiment && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Customer Sentiment</span>
                    <span className="font-semibold capitalize" style={{ color: SENTIMENT_COLORS[analysis!.customer_sentiment] ?? 'var(--text-primary)' }}>
                      {analysis!.customer_sentiment}
                    </span>
                  </div>
                )}
                {analysis!.customer_intent && (
                  <div className="flex gap-2 text-xs">
                    <span className="shrink-0" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>Customer Intent</span>
                    <span style={{ color: 'var(--text-primary)' }}>{analysis!.customer_intent}</span>
                  </div>
                )}
                {analysis!.key_topics && analysis!.key_topics.length > 0 && (
                  <div className="flex gap-2 text-xs flex-wrap pt-0.5">
                    {analysis!.key_topics.map(t => (
                      <span key={t} className="badge-info" style={{ fontSize: 11 }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Agent score */}
              {analysis!.agent_score && (
                <div
                  className="rounded-xl p-3 space-y-3"
                  style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
                >
                  <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
                    <Star className="h-3.5 w-3.5" /> Agent Score
                  </p>
                  <ScoreBar label="Overall"            value={analysis!.agent_score.overall} />
                  <ScoreBar label="Greeting"           value={analysis!.agent_score.greeting} />
                  <ScoreBar label="Product Knowledge"  value={analysis!.agent_score.product_knowledge} />
                  <ScoreBar label="Objection Handling" value={analysis!.agent_score.objection_handling} />
                  <ScoreBar label="Closing"            value={analysis!.agent_score.closing} />
                </div>
              )}

              {/* Coaching */}
              {(analysis!.coaching_notes || (analysis!.training_flags && analysis!.training_flags.length > 0)) && (
                <div
                  className="rounded-xl p-3 space-y-2"
                  style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
                >
                  <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Coaching
                  </p>
                  {analysis!.coaching_notes && (
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                      {analysis!.coaching_notes}
                    </p>
                  )}
                  {analysis!.training_flags && analysis!.training_flags.length > 0 && (
                    <ul className="space-y-1">
                      {analysis!.training_flags.map(f => (
                        <li key={f} className="flex items-start gap-1.5 text-xs" style={{ color: 'var(--warning-text)' }}>
                          <span className="mt-0.5 shrink-0">•</span>{f}
                        </li>
                      ))}
                    </ul>
                  )}
                  {analysis!.follow_up_action && (
                    <p className="text-xs font-medium mt-1" style={{ color: 'var(--brand-primary)' }}>
                      Follow-up: {analysis!.follow_up_action}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Transcript */}
          {hasTranscript && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
                <MessageCircle className="h-3.5 w-3.5" /> Transcript
              </p>
              <div
                className="rounded-xl p-3"
                style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
              >
                <pre
                  className="text-xs leading-relaxed whitespace-pre-wrap"
                  style={{ color: 'var(--text-primary)', fontFamily: 'inherit', margin: 0 }}
                >
                  {transcript}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
